import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import apiService from "../../../service/Api";
import { Proposal } from "../types/proposal.types";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import { SafeHtml } from "./components/safeHtml";

interface Service {
  id: number;
  name: string;
  description: string;
  price: number;
  customizable?: boolean;
  features?: string[];
}

interface ServicePageProps {
  proposal: Proposal;
}

interface PaginatedResponse<T> {
  results: T[];
}

const ServicePage: React.FC<ServicePageProps> = ({ proposal }) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const serviceRes = await apiService.get<PaginatedResponse<Service>>(
          "/api/packages/package-service/",
        );

        const backendServices = serviceRes.data.results || [];

        const formattedServices = backendServices.map((svc) => ({
          ...svc,
          price: Number(svc.price),
          customizable: svc.customizable || false,
          features: svc.features || [],
        }));

        setServices(formattedServices);
      } catch (error) {
        console.error("Failed to fetch:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Box
      id="service-page"
      sx={{
        p: 6,
        width: "816px",
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 2,
          pb: 1,
        }}
      >
        Alternate Packages and other ad-ons services
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading packages...
          </Typography>
        </Box>
      ) : (
        <>
          {proposal.alternatePackages &&
            proposal.alternatePackages.length > 0 && (
              <Box sx={{ mb: 5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                  }}
                >
                  {proposal.alternatePackages.map((pkg, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${reduxTheme.primary_colors[200]}`,
                        backgroundColor: "background.paper",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 2,
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                          color: reduxTheme.primary_colors[400],
                        }}
                      >
                        {pkg.name}
                      </Typography>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          mb: 1,
                        }}
                      >
                        ${pkg.price.toLocaleString()}
                      </Typography>

                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2,
                          fontSize: "0.8rem",
                          color: "text.secondary",
                        }}
                      >
                        {pkg.contents.map((item, i) => (
                          <Typography
                            component="li"
                            key={i}
                            variant="body2"
                            sx={{ mb: 0.3, fontSize: "0.75rem" }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
        </>
      )}

      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 2,
          pb: 1,
        }}
      >
        Services
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading services...
          </Typography>
        </Box>
      ) : services.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 6 }}>
          No services available at the moment.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {services.map((svc, index) => (
            <React.Fragment key={svc.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  py: 1.2,
                  px: 1,
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#fff",
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "background.paper" },
                }}
              >
                <Box sx={{ flex: 1, pr: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                  >
                    {svc.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      mt: 0.3,
                    }}
                  >
                    <SafeHtml html={svc.description} />
                    ...
                  </Typography>
                  {Array.isArray(svc.features) && svc.features.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      {svc.features.slice(0, 2).map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          sx={{
                            mr: 0.5,
                            mb: 0.5,
                            fontSize: "0.65rem",
                            bgcolor: "action.hover",
                          }}
                        />
                      ))}
                      {svc.features.length > 2 && (
                        <Chip
                          label={`+${svc.features.length - 2} more`}
                          size="small"
                          sx={{
                            fontSize: "0.65rem",
                            bgcolor: "#eee",
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    minWidth: "50px",
                    textAlign: "right",
                  }}
                >
                  ${svc.price}
                </Typography>
              </Box>

              {index < services.length - 1 && (
                <Divider sx={{ borderColor: "#f0f0f0" }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ServicePage;
